import { useConnectionAction } from "./useConnectionAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import {
  startSlackOAuth,
  updateIntegrationConnection,
  type IntegrationConnection,
} from "../../lib/integrations";
import { FormField } from "./FormField";
import { slackFormSchema, type SlackForm } from "./integrationFormSchemas";

export function SlackConnectionCard({
  connection,
  onSaved,
  onCancel,
}: {
  connection?: IntegrationConnection;
  onSaved: () => Promise<void>;
  onCancel: () => void;
}) {
  const action = useConnectionAction();
  const isSubmitting = action.pending;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SlackForm>({
    resolver: zodResolver(slackFormSchema),
    defaultValues: {
      name: connection?.name ?? "Slack",
      channelId: connection?.configSummary.channelId ?? "",
    },
  });

  const authorize = async (values: SlackForm) => {
    const { authorizationUrl } = await startSlackOAuth({
      ...values,
      ...(connection ? { connectionId: connection.id } : {}),
    });
    action.redirect(authorizationUrl);
  };

  const save = handleSubmit((values) =>
    action.run(connection ? "save" : "authorize", async () => {
      try {
        if (!connection) return await authorize(values);
        await updateIntegrationConnection(connection.id, {
          name: values.name,
          config: { channelId: values.channelId },
        });
        toast.success("Slack settings updated.");
        await onSaved();
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Unable to connect Slack.",
        );
      }
    }),
  );

  const reconnect = handleSubmit((values) =>
    action.run("authorize", async () => {
      try {
        await authorize(values);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Unable to authorize connection.",
        );
      }
    }),
  );

  return (
    <form className="space-y-4" onSubmit={save} noValidate>
      <div className="space-y-2">
        <p className="text-sm font-medium">Start with one customer channel.</p>
        <p className="text-sm leading-6 text-muted-foreground">
          In Slack, open the channel’s details and copy its link. Invite the IO
          Spoke app to that channel, then paste the link below.
        </p>
      </div>
      <FormField
        id="slack-channel"
        label="Slack channel link"
        registration={register("channelId")}
        error={errors.channelId}
        placeholder="https://your-workspace.slack.com/archives/…"
      />
      <details
        className="rounded-lg border px-4"
        open={errors.name ? true : undefined}
      >
        <summary className="min-h-11 cursor-pointer py-3 text-sm font-medium">
          Connection details and access
        </summary>
        <div className="space-y-4 pb-4">
          <FormField
            id="slack-name"
            label="Connection name"
            registration={register("name")}
            error={errors.name}
          />
          <p className="text-xs leading-5 text-muted-foreground">
            This connection reads up to 15 recent messages per sync from the
            selected channel. After messaging setup and account linking, you can
            explicitly send investigation questions and receive results in that
            channel. Other channel members can see those messages; only your
            linked account can answer. Sync scheduling is available after
            connection.
          </p>
        </div>
      </details>
      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          disabled={isSubmitting}
          loading={connection ? action.saving : action.authorizing}
          loadingLabel={connection ? "Saving…" : "Authorizing…"}
        >
          {connection ? (
            "Save settings"
          ) : (
            <>
              {" "}
              <ExternalLink className="mr-2 h-4 w-4" />
              Continue to Slack
            </>
          )}
        </Button>
        {connection ? (
          <Button
            type="button"
            variant="outline"
            onClick={reconnect}
            disabled={isSubmitting}
            loading={action.authorizing}
            loadingLabel="Authorizing…"
          >
            Reconnect Slack
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
